import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { ProgressProvider } from './context/ProgressContext';
import Navbar from './components/Navbar';
import PWAInstallBanner from './components/PWAInstallBanner';
import InstallPage from './pages/InstallPage';
import { registerUpdateChecker } from './utils/updateChecker';
import Home from './pages/Home';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import AddProduct from './pages/AddProduct';
import EditProduct from './pages/EditProduct';
import MyProducts from './pages/MyProducts';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import OrderConfirmation from './pages/OrderConfirmation';
import Orders from './pages/Orders';
import Login from './pages/Login';
import Register from './pages/Register';
import RegisterHub from './pages/RegisterHub';
import RegisterPickup from './pages/RegisterPickup';
import HubDashboard from './pages/HubDashboard';
import Hubs from './pages/Hubs';
import FarmSettings from './pages/FarmSettings';
import FarmerInbox from './pages/FarmerInbox';
import FarmerHubConnections from './pages/FarmerHubConnections';
import UserProfile from './pages/UserProfile';
import PickupDashboard from './pages/PickupDashboard';
import ProducerDashboard from './pages/ProducerDashboard';
import BuyerDashboard from './pages/BuyerDashboard';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminOrders from './pages/admin/AdminOrders';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import VerifyEmail from './pages/VerifyEmail';
import PrivateRoute from './components/PrivateRoute';
import ProducerRoute from './components/ProducerRoute';
import SetPassword from './pages/SetPassword';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PaymentReturn from './pages/PaymentReturn';
import ReconcileCredits from './pages/admin/ReconcileCredits';
import AdminOnly from './components/AdminOnly';
import ProducerStorefront from './pages/ProducerStorefront';
import HubStorefront from './pages/HubStorefront';
import KinDashboard from './pages/KinDashboard';
import KinAdminDashboard from './pages/KinAdminDashboard';
import KinConstructionKit from './components/KinConstructionKit';
import KinMemberOnboarding from './components/KinMemberOnboarding';
import MemberDraft from './components/MemberDraft';
import DraftCheckout from './components/DraftCheckout';
import KinStarter from './pages/KinStarter';
import KinCreate from './pages/KinCreate';
import RolesExplained from './pages/RolesExplained';
import AdminEngagement from './pages/admin/AdminEngagement';
import EducatorDashboard from './pages/EducatorDashboard';
import LearnModule from './pages/LearnModule';
import AdminEducation from './pages/admin/AdminEducation';
import AdminTwoFactor from './pages/admin/AdminTwoFactor';
import ClaimCredits from './pages/ClaimCredits';
import Learn from './pages/Learn';
import KinJoin from './pages/KinJoin';
import KinRegister from './pages/KinRegister';
import MyFavoritesPage from './pages/MyFavoritesPage';
import MyWishlistPage from './pages/MyWishlistPage';
import VersionBadge from './components/VersionBadge';
import AppUpdateBanner from './components/AppUpdateBanner';
import ProducersPage from './pages/Producers';
import AdminScanTerms from './pages/admin/AdminScanTerms';

// Component that handles role-based redirects
function AppRedirect() {
  const { user, loading, availableRoles } = useAuth();
  const [hasRedirected, setHasRedirected] = useState(false);

  useEffect(() => {
    // Only redirect when loading is complete, user is authenticated, and we haven't redirected yet
    if (!loading && user && !hasRedirected) {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const currentPath = window.location.pathname;

          // Only redirect if on root or login page
          if (currentPath === '/' || currentPath === '/login') {
            // Wait a moment for availableRoles to be fully loaded
            setTimeout(() => {
              // Priority 1: Kin Coordinator
              if (availableRoles && availableRoles.includes('kin_coordinator')) {
                setHasRedirected(true);
                window.location.href = '/kin-dashboard';
                return;
              }

              // Priority 2: Regular roles
              const role = user.role;
              if (role === 'producer') {
                setHasRedirected(true);
                window.location.href = '/producer-dashboard';
              } else if (role === 'hub') {
                setHasRedirected(true);
                window.location.href = '/hub-dashboard';
              } else if (role === 'pickup') {
                setHasRedirected(true);
                window.location.href = '/pickup-dashboard';
              } else if (role === 'admin') {
                setHasRedirected(true);
                window.location.href = '/admin';
              } else if (role === 'educator') {
                setHasRedirected(true);
                window.location.href = '/educator-dashboard';
              } else {
                // Default to buyer dashboard
                setHasRedirected(true);
                window.location.href = '/buyer/dashboard';
              }
            }, 300);
          }
        } catch (e) {
          // If token is invalid, stay on current page
          console.log('Invalid token, staying on current page');
        }
      }
    }
  }, [user, loading, availableRoles, hasRedirected]);

  return null;
}

function App() {
  // Initialize PWA update checker
  useEffect(() => {
    registerUpdateChecker();
  }, []);

  return (
    <Router>
      <AuthProvider>
       <ProgressProvider>
        <CartProvider>
          <AppRedirect />
          <div className="min-h-screen bg-gray-50">
            <Navbar />
            <PWAInstallBanner />
            <main className="container mx-auto px-4 py-8">
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Home />} />
                <Route path="/learn" element={<Learn />} />
                <Route path="/products" element={<Products />} />
                <Route path="/product/:id" element={<ProductDetail />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/register/hub" element={<RegisterHub />} />
                <Route path="/register/pickup" element={<RegisterPickup />} />
                <Route path="/hubs" element={<Hubs />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/set-password" element={<PrivateRoute><SetPassword /></PrivateRoute>} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/payments/return" element={<PaymentReturn mode="complete" />} />
                <Route path="/payments/refresh" element={<PaymentReturn mode="refresh" />} />
                <Route path="/producer/:slug" element={<ProducerStorefront />} />
                <Route path="/hub/:slug" element={<HubStorefront />} />
                <Route path="/test" element={<div>TEST ROUTE WORKS</div>} />
                <Route path="/roles-explained" element={<RolesExplained />} />
                <Route path="/producers" element={<ProducersPage />} />

                {/* Cart & Checkout */}
                <Route path="/cart" element={<PrivateRoute><Cart /></PrivateRoute>} />
                <Route path="/checkout" element={<PrivateRoute><Checkout /></PrivateRoute>} />
                <Route path="/order-confirmation/:id" element={<PrivateRoute><OrderConfirmation /></PrivateRoute>} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/payment-cancel" element={<PrivateRoute><PaymentCancel /></PrivateRoute>} />

                {/* Buyer Routes */}
                <Route path="/buyer/dashboard" element={<PrivateRoute><BuyerDashboard /></PrivateRoute>} />
                <Route path="/orders" element={<PrivateRoute><Orders /></PrivateRoute>} />
                <Route path="/profile" element={<PrivateRoute><UserProfile /></PrivateRoute>} />
                <Route path="/favorites" element={<PrivateRoute><MyFavoritesPage /></PrivateRoute>} />
                <Route path="/wishlist" element={<PrivateRoute><MyWishlistPage /></PrivateRoute>} />

                {/* Producer Routes */}
                <Route path="/producer-dashboard" element={<PrivateRoute><ProducerDashboard /></PrivateRoute>} />
                <Route path="/add-product" element={<ProducerRoute><AddProduct /></ProducerRoute>} />
                <Route path="/my-products" element={<ProducerRoute><MyProducts /></ProducerRoute>} />
                <Route path="/edit-product/:id" element={<ProducerRoute><EditProduct /></ProducerRoute>} />
                <Route path="/farm-settings" element={<ProducerRoute><FarmSettings /></ProducerRoute>} />
                <Route path="/farmer-inbox" element={<ProducerRoute><FarmerInbox /></ProducerRoute>} />
                <Route path="/farmer-hub-connections" element={<ProducerRoute><FarmerHubConnections /></ProducerRoute>} />

                {/* Hub Routes */}
                <Route path="/hub-dashboard" element={<PrivateRoute><HubDashboard /></PrivateRoute>} />
                <Route path="/hub/invites" element={<PrivateRoute><HubDashboard /></PrivateRoute>} />

                {/* Pickup Routes */}
                <Route path="/pickup-dashboard" element={<PrivateRoute><PickupDashboard /></PrivateRoute>} />

                {/* Kin Routes */}
                <Route path="/kin-dashboard" element={<PrivateRoute><KinDashboard /></PrivateRoute>} />
                <Route path="/kin-admin" element={<PrivateRoute><KinAdminDashboard /></PrivateRoute>} />
                <Route path="/kin-admin/:kinId" element={<PrivateRoute><KinAdminDashboard /></PrivateRoute>} />
                <Route path="/kin-construction" element={<PrivateRoute><KinConstructionKit /></PrivateRoute>} />
                <Route path="/member-draft/:kinId" element={<PrivateRoute><MemberDraft /></PrivateRoute>} />
                <Route path="/draft-checkout/:kinId" element={<PrivateRoute><DraftCheckout /></PrivateRoute>} />
                <Route path="/kin-starter" element={<PrivateRoute><KinStarter /></PrivateRoute>} />
                <Route path="/kin-create" element={<PrivateRoute><KinCreate /></PrivateRoute>} />
                <Route path="/kin-join" element={<KinJoin />} />
                <Route path="/kin-register" element={<KinRegister />} />

                {/* Educator Routes */}
                <Route path="/educator-dashboard" element={<PrivateRoute><EducatorDashboard /></PrivateRoute>} />
                <Route path="/learn/:slug" element={<LearnModule />} />
                <Route path="/claim-credits" element={<ClaimCredits />} />

                {/* Admin Routes */}
                <Route path="/admin/2fa" element={<PrivateRoute><AdminTwoFactor /></PrivateRoute>} />
                <Route path="/admin" element={<PrivateRoute><AdminDashboard /></PrivateRoute>} />
                <Route path="/admin/orders" element={<PrivateRoute><AdminOrders /></PrivateRoute>} />
                <Route path="/admin/analytics" element={<PrivateRoute><AdminAnalytics /></PrivateRoute>} />
                <Route path="/admin/reconcile" element={<ReconcileCredits />} />
                <Route path="/admin/engagement" element={<PrivateRoute><AdminEngagement /></PrivateRoute>} />
                <Route path="/admin/education" element={<PrivateRoute><AdminEducation /></PrivateRoute>} />
                <Route path="/admin/scan-terms" element={<PrivateRoute><AdminScanTerms /></PrivateRoute>} />

                {/* PWA Install */}
                <Route path="/install" element={<InstallPage />} />
              </Routes>
            </main>
              <VersionBadge />
              <AppUpdateBanner />
          </div>
         <Toaster position="top-right" />
        </CartProvider>
       </ProgressProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
